import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '../lib/utils';
import { CardUI } from './CardUI';
import { useGameBoard } from './GameBoardContext';

interface OpponentHandProps {
  playerIndex: number;
  position: 'top' | 'left' | 'right';
}

export const OpponentHand: React.FC<OpponentHandProps> = ({ playerIndex, position }) => {
  const { gameState, dealId, revealedCardIds, revealingCardIds, viewportWidth } = useGameBoard();
  const player = gameState.players[playerIndex];
  const opponentScale = viewportWidth < 360 ? 0.5 : viewportWidth < 400 ? 0.58 : 0.65;

  if (!player) return null;

  const isCurrentTurn = gameState.currentPlayerIndex === playerIndex;

  // Render position-specific layouts
  if (position === 'top') {
    return (
      <div 
        id={`opponent-hand-${playerIndex}`} 
        className={cn(
          "flex flex-col items-center gap-0.5 z-20 pt-2 pb-1 px-3 rounded-2xl transition-all duration-300",
          isCurrentTurn ? "glass-panel-amber active-glow-panel scale-[1.02]" : "border border-transparent"
        )}
      >
        <div className="flex items-center gap-1 mb-0.5">
          <div className="text-[8px] text-white/30 uppercase tracking-widest font-black">
            {player.name || 'Avversario'}
          </div>
          <span className="text-[9px] text-white/40 font-bold bg-white/10 px-1.5 py-0.5 rounded-full">
            {player.hand.length}
          </span>
          <AnimatePresence>
            {isCurrentTurn && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="text-[8px] text-cyan-400 font-black uppercase tracking-widest flex items-center gap-0.5"
              >
                <span className="inline-flex gap-0.5">
                  <span className="animate-bounce" style={{ animationDelay: '0ms' }}>●</span>
                  <span className="animate-bounce" style={{ animationDelay: '150ms' }}>●</span>
                  <span className="animate-bounce" style={{ animationDelay: '300ms' }}>●</span>
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="relative" style={{ width: `${Math.round(110 * (opponentScale / 0.65))}px`, height: `${Math.round(65 * (opponentScale / 0.65))}px`, overflow: 'visible' }}>
          <div className="absolute inset-0 flex items-start justify-center">
            {player.hand.map((card, i) => {
              const rotation = (i - Math.floor(player.hand.length / 2)) * 4;
              const baseMarginLeft = Math.round(-24 * (opponentScale / 0.65));
              return (
                <div
                  key={`${dealId}-top-${card.id}`}
                  style={{
                    transform: `rotate(${rotation}deg)`,
                    marginLeft: i === 0 ? 0 : `${baseMarginLeft}px`,
                    zIndex: i,
                    transformOrigin: 'top center',
                  }}
                >
                  <CardUI
                    card={card}
                    isHidden={!revealedCardIds.has(card.id) && !revealingCardIds.has(card.id)}
                    isRevealing={revealingCardIds.has(card.id)}
                    scale={opponentScale}
                    disabled
                    className="opacity-50"
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Left or Right layouts
  const isLeft = position === 'left';
  const widthVal = '45px';
  const heightVal = '80px';
  const outerClasses = isLeft
    ? "flex flex-col items-center gap-0.5 z-20 absolute left-0 top-1/2 -translate-y-1/2 p-2 rounded-2xl transition-all duration-300"
    : "flex flex-col items-center gap-0.5 z-20 absolute right-0 top-1/2 -translate-y-1/2 p-2 rounded-2xl transition-all duration-300";

  return (
    <div 
      id={`opponent-hand-${playerIndex}`} 
      className={cn(
        outerClasses,
        isCurrentTurn ? "glass-panel-amber active-glow-panel scale-[1.02]" : "border border-transparent"
      )}
    >
      <div className="flex items-center gap-1 mb-0.5">
        <div className="text-[8px] text-white/30 uppercase tracking-widest font-black">
          {player.name || 'Avversario'}
        </div>
        <span className="text-[9px] text-white/40 font-bold bg-white/10 px-1.5 py-0.5 rounded-full">
          {player.hand.length}
        </span>
        <AnimatePresence>
          {isCurrentTurn && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="text-[8px] text-cyan-400 font-black uppercase tracking-widest flex items-center gap-0.5"
            >
              <span className="inline-flex gap-0.5">
                <span className="animate-bounce" style={{ animationDelay: '0ms' }}>●</span>
                <span className="animate-bounce" style={{ animationDelay: '150ms' }}>●</span>
                <span className="animate-bounce" style={{ animationDelay: '300ms' }}>●</span>
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <div 
        className="relative" 
        style={{ 
          width: `${Math.round(45 * (opponentScale / 0.65))}px`, 
          height: `${Math.round(80 * (opponentScale / 0.65))}px`, 
          overflow: 'visible' 
        }}
      >
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {player.hand.map((card, i) => {
            const rotation = (i - Math.floor(player.hand.length / 2)) * 4;
            const baseMarginTop = Math.round(-32 * (opponentScale / 0.65));
            return (
              <div
                key={`${dealId}-${position}-${card.id}`}
                style={{
                  transform: `rotate(${rotation}deg)`,
                  marginTop: i === 0 ? 0 : `${baseMarginTop}px`,
                  zIndex: i,
                  transformOrigin: 'center',
                }}
              >
                <CardUI
                  card={card}
                  isHidden={!revealedCardIds.has(card.id) && !revealingCardIds.has(card.id)}
                  isRevealing={revealingCardIds.has(card.id)}
                  scale={opponentScale}
                  disabled
                  className="opacity-50"
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
